import { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import styles from './searchable-select.module.scss';

export type SearchableSelectArrayOption = {
  label: string;
  value: string[] | undefined;
};

export type SearchableSelectArrayOptionGroup = {
  groupLabel: string;
  value: SearchableSelectArrayOption[];
};

/* same as SearchableSelect: keep focus in the input when the list's own scrollbar or padding is
 * pressed, so onBlur does not close the list mid-scroll; option presses are unchanged */
const keepFocusOnContainerPress: React.MouseEventHandler<HTMLDivElement> = e => {
  if (e.target === e.currentTarget) e.preventDefault();
};

type SearchableSelectProps = {
  onValueSelected: (val: string[]) => void;
  value: string | undefined;
  options: SearchableSelectArrayOption[] | SearchableSelectArrayOptionGroup[];
  disabled?: boolean;
  required?: boolean;
  inputClass?: string;
  containerClass?: string;
  ['data-cy']?: string;
};

const SearchableSelectArray: FC<SearchableSelectProps> = ({
  options,
  disabled,
  required,
  value,
  onValueSelected,
  inputClass,
  containerClass,
  ...props
}) => {
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [textValue, setTextValue] = useState(value);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isGrouped = (opts: SearchableSelectArrayOption[] | SearchableSelectArrayOptionGroup[]): boolean => {
    return opts.some(opt => Object.keys(opt).includes('groupLabel'));
  };

  const setToInputValue = useCallback(() => {
    let option: SearchableSelectArrayOption | undefined;
    if (isGrouped(options)) {
      // Grouped
      option = (options as SearchableSelectArrayOptionGroup[])
        .flatMap(opt => opt.value)
        .find(opt => opt.label === value);
    } else {
      option = (options as SearchableSelectArrayOption[]).find(o => o.label === value);
    }
    /* retired/inactive codes have no matching (active-only) option, so fall back to the raw value
     * the consumer passed (the stored code) instead of going blank — preserves zero-churn display */
    setTextValue(option?.label ?? value ?? '');
    setSearching(false);
  }, [options, value]);

  const filterOptions = useCallback(
    (
      searchString: string,
      opts: SearchableSelectArrayOption[] | SearchableSelectArrayOptionGroup[]
    ): SearchableSelectArrayOption[] | SearchableSelectArrayOptionGroup[] => {
      if (isGrouped(opts)) {
        // Grouped
        const filteredGroups: SearchableSelectArrayOptionGroup[] = (opts as SearchableSelectArrayOptionGroup[])
          .map(opt => ({
            ...opt,
            value: opt.value.filter(val => val.label.toLowerCase().startsWith(searchString.toLowerCase()))
          }))
          .filter(opt => !!opt.value.length);
        return filteredGroups;
      } else {
        // Ungrouped
        return (opts as SearchableSelectArrayOption[]).filter(opt =>
          opt.label.toLowerCase().startsWith(searchString.toLowerCase())
        );
      }
    },
    []
  );

  const onFocus = useCallback<React.FocusEventHandler<HTMLInputElement>>(() => {
    setFilteredOptions(options);
    setSearching(true);
  }, [options]);

  const onTextChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    e => {
      setTextValue(e.currentTarget.value);
      setFilteredOptions(filterOptions(e.currentTarget.value, options));
    },
    [filterOptions, options]
  );

  const onOptionClicked = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    e => {
      const val = e.currentTarget.dataset['value']?.split(',');
      onValueSelected(val ?? []);
      setTextValue(e.currentTarget.textContent ?? '');
      setSearching(false);
    },
    [onValueSelected]
  );

  const onBlur = useCallback<React.FocusEventHandler<HTMLInputElement>>(
    e => {
      if (!e.relatedTarget?.classList.contains(styles['dropdown-option'])) {
        setToInputValue();
      }
    },
    [setToInputValue]
  );

  useEffect(() => {
    setToInputValue();
  }, [setToInputValue]);

  return (
    <div className={styles['searchable-select'] + ' ' + (containerClass ?? '')}>
      <input
        ref={inputRef}
        type="text"
        disabled={!!disabled}
        value={textValue ?? ''}
        onFocus={onFocus}
        onChange={onTextChange}
        onBlur={onBlur}
        required={!!required}
        className={`${!textValue ? 'blank' : ''} ${inputClass ?? ''}`.trim()}
        data-cy={props['data-cy']}
      />
      {!disabled && Boolean(textValue || value) && (
        <svg
          className={styles['clear-btn']}
          focusable="false"
          aria-hidden="true"
          viewBox="0 0 24 24"
          onClick={() => {
            onValueSelected([]);
            setTextValue('');
            setFilteredOptions(options);
            setTimeout(() => {
              inputRef.current?.focus();
            }, 0);
          }}
        >
          <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
        </svg>
      )}
      {searching && (
        <div className={styles.dropdown} onMouseDown={keepFocusOnContainerPress}>
          {!isGrouped(filteredOptions) &&
            (filteredOptions as SearchableSelectArrayOption[]).map((option, i) => (
              <div
                key={option.value?.join('-')}
                tabIndex={i}
                className={`pointer ${styles['dropdown-option']}`}
                onClick={onOptionClicked}
                data-value={option.value?.join(',')}
                data-cy={`${props['data-cy'] ?? 'searchable-select'}-option-${option.value?.join('-') ?? ''}`}
              >
                {option.label}
              </div>
            ))}
          {isGrouped(filteredOptions) &&
            (filteredOptions as SearchableSelectArrayOptionGroup[]).map((group, i) => (
              <Fragment key={group.groupLabel}>
                <div className={`${styles['dropdown-group-header']}`}>{group.groupLabel}</div>
                {group.value.map((option, j) => (
                  <div
                    key={option.value?.join('-')}
                    tabIndex={i * filteredOptions.length + j}
                    className={`pointer ${styles['dropdown-option']}`}
                    onClick={onOptionClicked}
                    data-value={option.value}
                    data-cy={`${props['data-cy'] ?? 'searchable-select'}-option-${option.value?.join('-') ?? ''}`}
                  >
                    {option.label}
                  </div>
                ))}
              </Fragment>
            ))}
        </div>
      )}
    </div>
  );
};

export default SearchableSelectArray;
